import { ChevronRight, Pencil, Plus, Search, UserPlus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAdminClientsGetAdminClients } from '@/lib/api/admin-clients/admin-clients';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useHasRole } from '@/lib/auth/useAuth';
import { CreateClientDialog } from './CreateClientDialog';
import { EditClientDialog } from './EditClientDialog';
import { PortalUsersAdminDialog } from './PortalUsersAdminDialog';

export default function ClientsPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [pendingSearch, setPendingSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [portalUsersFor, setPortalUsersFor] = useState<{ id: string; name: string } | null>(null);

  const navigate = useNavigate();
  const canCreate = useHasRole('Admin');
  const canEdit = canCreate;
  const canSeedPortalUsers = canCreate;

  const { data, isLoading, isError, error } = useAdminClientsGetAdminClients({
    page,
    limit: 25,
    search: search || undefined,
  });

  const pages = useMemo(() => {
    if (!data) return 1;
    const total = Number(data.total);
    const limit = Number(data.limit) || 1;
    return Math.max(1, Math.ceil(total / limit));
  }, [data]);

  return (
    <section className="space-y-5">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Clients</h1>
          <p className="text-sm text-muted-foreground">Partner integrations and their rate limits.</p>
        </div>
        {canCreate && (
          <Button onClick={() => setCreateOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            New client
          </Button>
        )}
      </header>

      <Card>
        <CardHeader className="flex flex-row items-center gap-3 space-y-0">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input
            value={pendingSearch}
            placeholder="Search by name or client-id…"
            onChange={(e) => setPendingSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setPage(1);
                setSearch(pendingSearch.trim());
              }
            }}
            className="max-w-sm"
          />
          {search && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearch('');
                setPendingSearch('');
                setPage(1);
              }}
            >
              Clear
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {isError ? (
            <div className="p-6">
              <CardTitle className="text-destructive">Could not load clients</CardTitle>
              <CardDescription>{(error as Error)?.message ?? 'Unknown error'}</CardDescription>
            </div>
          ) : isLoading ? (
            <div className="space-y-2 p-6">
              {Array.from({ length: 5 }, (_, i) => (
                <div key={i} className="h-10 animate-pulse rounded-md bg-muted" />
              ))}
            </div>
          ) : !data || data.items.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-sm text-muted-foreground">
                No clients match the current filter.
                {canCreate && ' Create the first one to start issuing tokens.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="p-3 font-medium">Name</th>
                    <th className="p-3 font-medium">Client ID</th>
                    <th className="p-3 font-medium">Rate limit / min</th>
                    <th className="p-3 font-medium">Status</th>
                    <th className="p-3 font-medium">Created</th>
                    {canEdit && <th className="w-28 p-3" />}
                    <th className="w-8 p-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.items.map((c) => (
                    <tr
                      key={c.id}
                      className="cursor-pointer hover:bg-muted/30"
                      onClick={() => navigate(`/clients/${c.id}`)}
                    >
                      <td className="p-3 font-medium">{c.name}</td>
                      <td className="p-3 font-mono text-xs text-muted-foreground">{c.clientId}</td>
                      <td className="p-3 tabular-nums">{c.rateLimitPerMin}</td>
                      <td className="p-3">
                        {c.isActive ? (
                          <Badge variant="success">Active</Badge>
                        ) : (
                          <Badge variant="warning">Suspended</Badge>
                        )}
                      </td>
                      <td className="p-3 text-xs text-muted-foreground">
                        {new Date(c.createdAt).toLocaleDateString()}
                      </td>
                      {canEdit && (
                        <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end gap-1">
                            {canSeedPortalUsers && (
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={`Portal users for ${c.name}`}
                                title="Portal users"
                                onClick={() => setPortalUsersFor({ id: c.id, name: c.name })}
                              >
                                <UserPlus className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Edit ${c.name}`}
                              onClick={() => setEditingId(c.id)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      )}
                      <td className="p-3 text-muted-foreground">
                        <ChevronRight className="h-4 w-4" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
        {data && data.total > data.limit && (
          <div className="flex items-center justify-between border-t border-border p-3 text-xs text-muted-foreground">
            <span>
              Page {page} of {pages} · {data.total} total
            </span>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
                disabled={page >= pages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {canCreate && <CreateClientDialog open={createOpen} onOpenChange={setCreateOpen} />}
      {canEdit && (
        <EditClientDialog
          clientId={editingId}
          onOpenChange={(open) => !open && setEditingId(null)}
        />
      )}
      {canSeedPortalUsers && (
        <PortalUsersAdminDialog
          clientId={portalUsersFor?.id ?? null}
          clientName={portalUsersFor?.name}
          onOpenChange={(open) => !open && setPortalUsersFor(null)}
        />
      )}
    </section>
  );
}
